import type { MouseEvent, ReactNode } from 'react';
import { useTranslation } from 'react-i18next';

import styles from './CellFilterButton.module.scss';

interface CellFilterButtonProps {
    /** The value as the row shows it, for the "Show only ..." hint. */
    value: string;
    /** More of the value than the hint says, such as the whole user agent. */
    detail?: string;
    onFilter: () => void;
    children: ReactNode;
}

/**
 * A mouse shortcut on a value. It stays out of the tab order, because a page of rows
 * times six of these would bury everything after the table; the keyboard path is the
 * time button and the details dialog. It is a sibling of the others, never nested, and
 * the click stops at it so the row does not open the details too.
 */
export default function CellFilterButton(props: CellFilterButtonProps) {
    const { value, detail, onFilter, children } = props;
    const { t } = useTranslation();
    const hint = t('auditLog.table.showOnly', { value });

    function handleClick(event: MouseEvent<HTMLButtonElement>) {
        event.stopPropagation();
        onFilter();
    }

    return (
        <button
            type="button"
            tabIndex={-1}
            className={styles.filterButton}
            title={detail === undefined || detail === value ? hint : `${detail}\n${hint}`}
            onClick={handleClick}>
            {children}
        </button>
    );
}
